<template>
    <div class="power-menu" :style="menuStyle">
        <v-fade-transition>
            <div v-if="open" class="power-menu__surface" :style="surfaceStyle" @click.self="close">
                <button
                    v-for="item in positionedItems"
                    :key="item.key"
                    type="button"
                    class="power-menu__item"
                    :style="item.style"
                    :aria-label="item.title"
                    @click="select(item)"
                >
                    <span class="power-menu__circle" :style="circleStyle">
                        <v-icon :size="sizes.icon" :color="theme.headerFooterColor">{{ item.icon }}</v-icon>
                    </span>
                    <span class="power-menu__label" :style="{ color: theme.footerIconSelectedColor }">{{ item.title }}</span>
                </button>
            </div>
        </v-fade-transition>
        <button
            type="button"
            class="power-menu__button"
            :style="buttonStyle"
            :aria-label="$t('more')"
            :aria-expanded="open ? 'true' : 'false'"
            @click="toggle"
        >
            <v-icon size="28" :color="theme.headerFooterColor">mdi-dots-horizontal</v-icon>
        </button>
    </div>
</template>

<script>
import { layoutMixin } from '../../utils/layout-mixin';
import { withAlpha } from '../../utils/color.mjs';
import { arcLayout } from '../../utils/power-menu-layout.mjs';

const MAX_PER_ROW = 7;
const BUTTON_SIZE = 48; // px, keep in sync with the stylesheet below
const BASE_OFFSET = 50; // px between the bar edge and the arc center
const EDGE_MARGIN = 8; // px kept free at the sides and the top of the surface
const BUTTON_MARGIN = 4; // px kept free around the button and above the bar edge
const ROW_GAP = 8; // px between stacked rows
const LABEL_GAP = 4;
const LABEL_LINES = 2;
const LABEL_LINE_HEIGHT = 1.25;
// item sizes below and from the md breakpoint; the stylesheet reads them as CSS variables
const SIZES = {
    small: { circle: 50, icon: 26, width: 84, fontSize: 10, minRadius: 70 },
    large: { circle: 60, icon: 30, width: 100, fontSize: 14, minRadius: 90 },
};

/**
 * Round button over the middle of the footer bar that unfolds the overflow entries and the
 * checkin actions on a half-circle above it, like the native power button. Emits navigate(entry)
 * for pages and action(key) for actions; closes on selection, tap outside, route change and
 * Escape.
 */
export default {
    mixins: [layoutMixin],
    props: {
        entries: {
            type: Array,
            default: () => [],
        },
        actions: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            open: false,
            surfaceWidth: 0,
            surfaceHeight: 0,
        };
    },
    watch: {
        $route() {
            this.close();
        },
        open(val) {
            if (val) {
                document.addEventListener('keydown', this.onKeydown);
                document.addEventListener('click', this.onDocumentClick, true);
                window.addEventListener('resize', this.measureSurface);
            } else {
                this.removeListeners();
            }
        },
    },
    computed: {
        // the app bar is registered with Vuetify, the bar height is the one of Footer.vue
        menuStyle() {
            return { top: `${this.$vuetify.application.top}px` };
        },
        surfaceStyle() {
            return {
                backgroundColor: withAlpha(this.theme.headerFooterColor, 0.8),
                '--item-width': `${this.sizes.width}px`,
                '--circle-size': `${this.sizes.circle}px`,
                '--label-font-size': `${this.sizes.fontSize}px`,
                '--label-line-height': LABEL_LINE_HEIGHT,
                '--label-lines': LABEL_LINES,
                '--label-gap': `${LABEL_GAP}px`,
            };
        },
        buttonStyle() {
            return {
                backgroundColor: this.theme.footerIconSelectedColor,
                borderColor: this.theme.headerFooterColor,
            };
        },
        circleStyle() {
            return {
                backgroundColor: this.theme.footerIconColor,
                borderColor: this.theme.footerIconSelectedColor,
            };
        },
        sizes() {
            return this.isMdAndUpDevice ? SIZES.large : SIZES.small;
        },
        itemHeight() {
            return this.sizes.circle + LABEL_GAP + LABEL_LINES * this.sizes.fontSize * LABEL_LINE_HEIGHT;
        },
        items() {
            return this.entries
                .map((entry) => ({ ...entry, type: 'page' }))
                .concat(this.actions.map((action) => ({ ...action, type: 'action' })));
        },
        // items with their position on the arc rows (power-menu-layout.mjs), the first item left
        positionedItems() {
            const { width, minRadius } = this.sizes;
            const maxRadius =
                Math.min(this.surfaceWidth / 2 - width / 2, this.surfaceHeight - BASE_OFFSET - this.itemHeight / 2) - EDGE_MARGIN;
            const positions = arcLayout({
                count: this.items.length,
                itemWidth: width,
                itemHeight: this.itemHeight,
                minRadius,
                maxRadius,
                minBottom: BUTTON_MARGIN - BASE_OFFSET,
                keepOut: { x: 0, y: -BASE_OFFSET, width: BUTTON_SIZE + 2 * BUTTON_MARGIN, height: BUTTON_SIZE + 2 * BUTTON_MARGIN },
                maxPerRow: MAX_PER_ROW,
                rowGap: ROW_GAP,
            });
            return this.items.map((item, index) => ({
                ...item,
                style: {
                    left: `calc(50% + ${positions[index].x}px)`,
                    bottom: `${BASE_OFFSET + positions[index].y}px`,
                },
            }));
        },
    },
    methods: {
        toggle() {
            if (!this.open) {
                this.measureSurface();
            }
            this.open = !this.open;
        },
        close() {
            this.open = false;
        },
        select(item) {
            this.close();
            if (item.type === 'action') {
                this.$emit('action', item.key);
            } else {
                this.$emit('navigate', item);
            }
        },
        // the root element spans the surface between app bar and footer bar even while closed
        measureSurface() {
            this.surfaceWidth = this.$el.clientWidth;
            this.surfaceHeight = this.$el.clientHeight;
        },
        onKeydown(event) {
            if (event.key === 'Escape') {
                this.close();
            }
        },
        // a tap on the footer bar (or anywhere outside the menu) closes it
        onDocumentClick(event) {
            if (!this.$el.contains(event.target)) {
                this.close();
            }
        },
        removeListeners() {
            document.removeEventListener('keydown', this.onKeydown);
            document.removeEventListener('click', this.onDocumentClick, true);
            window.removeEventListener('resize', this.measureSurface);
        },
    },
    beforeDestroy() {
        this.removeListeners();
    },
};
</script>

<style lang="scss" scoped>
/* covers the page between app bar and footer bar (51 px, Footer.vue); below the welcome
   overlay (z-index 6), above the page content and the task list drawer */
.power-menu {
    position: absolute;
    left: 0;
    right: 0;
    bottom: calc(51px + env(safe-area-inset-bottom));
    z-index: 5;
    pointer-events: none;
}
.power-menu__surface {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    pointer-events: auto;
}
/* centered on the bar edge, size BUTTON_SIZE of the script */
.power-menu__button {
    position: absolute;
    left: 50%;
    bottom: -24px;
    transform: translateX(-50%);
    width: 48px;
    height: 48px;
    border-radius: 50%;
    border: 3px solid;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.4);
    cursor: pointer;
    pointer-events: auto;
    outline: none;
    -webkit-tap-highlight-color: transparent;
}
/* box of circle, gap and label, centered on its position; the sizes are the surface variables */
.power-menu__item {
    position: absolute;
    width: var(--item-width);
    transform: translate(-50%, 50%);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--label-gap);
    background: none;
    border: 0;
    padding: 0;
    cursor: pointer;
    outline: none;
    -webkit-tap-highlight-color: transparent;
}
.power-menu__circle {
    width: var(--circle-size);
    height: var(--circle-size);
    border-radius: 50%;
    border: 1px solid;
    display: flex;
    align-items: center;
    justify-content: center;
}
/* at most two lines under the circle (the native app does not limit the label) */
.power-menu__label {
    width: 100%;
    font-size: var(--label-font-size);
    line-height: var(--label-line-height);
    text-align: center;
    display: -webkit-box;
    -webkit-line-clamp: var(--label-lines);
    -webkit-box-orient: vertical;
    overflow: hidden;
    overflow-wrap: anywhere;
}
</style>
